import React, { FC, useEffect, useRef, useState } from "react";

// Hooks
import { StepName, useFormUI } from "../Hooks/form";
import { getRootData } from "../Hooks/useStepData";
import { useLabels } from "../Hooks/useLabels";
import { usePersistedSelection } from "../Hooks/usePersistedSelection";
import { useBookingContext } from "../../BookingButton/BookingContext/BookingContext";
import { useFilteredTreatment } from "../Hooks/useFilteredTreatment";

// Steps
import { InitialStep } from "../Steps/InitialStep/InitialStep";
import { TreatmentStep } from "../Steps/TreatmentStep/TreatmentStep";
import { AppointmentStep } from "../Steps/AppointmentStep/AppointmentStep";
import { PersonalInfoFormStep } from "../Steps/PersonalInfoFormStep/PersonalInfoFormStep";
import { BookingConfirmedStep } from "../Steps/BookingConfirmedStep/BookingConfirmedStep";

// Types
import { RootDataType } from "../Helpers/interfaces";
import { QuickAppointmentSetupDto } from "../../BookingButton/QuickAppointmentSetupDto.csharp";

//Hepers
import { assertNever, isObjectEmpty } from "../Helpers";
import {
  findTreatmentById,
  selectContainer,
  isServiceContainer,
} from "../Helpers/treatmentsHelpers";
import { parentTreatmentInitialState } from "../Helpers/initialState/parentTreatmentInitialState";

// Elements
import { GoBackButton } from "../GoBackButton/GoBackButton";
import { RichTextArea } from "../RichTextArea/RichTextArea";
import { LoadingAnimation } from "../../LoadingAnimation/LoadingAnimation";
import { StorageAccessor } from "../StorageAccesor/StorageAccesor";
import { FormDispatch, FormFields, FormReducer } from "../Helpers/formTypes";
import Modal from "react-modal";
import { GtmService } from "../GtmService";
import { ProgressBar } from "../ProgressBar/ProgressBar";
import { CloseButton } from "../CloseButton/CloseButton";
import { useTrackEvent } from "@optimizely/react-sdk";
import { LocationDto } from "@apiModels/LocationDto.csharp";
import { TreatmentsContainerDto } from "@apiModels/TreatmentsContainerDto.csharp";
import { DoctorDto } from "@apiModels/DoctorDto.csharp";
import { ServiceContainerDto } from "@apiModels/ServiceContainerDto.csharp";
import { ModalThemeColor } from "../../../../Model/ModalThemeColor.csharp";
import classNames from "classnames";
import { selectDoctorAndLocation } from "../Helpers/treatmentDoctorHelpers";
import { useFilteredTreatments } from "../Hooks/useFilteredTreatments";

export const Booking: FC<{
  showCloseButton?: boolean;
  closeModal: () => void;
  autostartBooking: boolean;
  breadcrumbs: string[];
  ignorePersistedData?: boolean;
  quickAppointmentSetup?: QuickAppointmentSetupDto;
  modalThemeColor?: ModalThemeColor;
}> = ({
  showCloseButton,
  closeModal,
  autostartBooking,
  breadcrumbs,
  ignorePersistedData,
  quickAppointmentSetup,
  modalThemeColor,
}) => {
  const [rootData, setRootData] = useState<RootDataType>({
    treatments: [],
    centers: [],
    services: [],
    doctors: [],
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const {
    state,
    dispatch,
    currentStep,
    currentStepName,
    steps,
    nextStep,
    previousStep,
    setStep,
    setStepByName,
  } = useFormUI();

  const currentStepRef = useRef(currentStep);
  const { bookingLabels } = useLabels();
  const {
    waitlist,
    newBookingFilterValues,
    optimizelyExperimentation,
    trackAbEvent,
  } = useBookingContext();
  const { containerId, doctorId, locationId, fixedService } =
    newBookingFilterValues ?? {};

  const [track, clientReady, didTimeout] = useTrackEvent();

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentStepName !== "BookingConfirmedStep") {
        GtmService.bookingDroppedOff();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (trackAbEvent && clientReady) {
      if (optimizelyExperimentation?.events.bookingOpened) {
        track(optimizelyExperimentation.events.bookingOpened);
      }
    }
  }, [clientReady]);

  const modalOpenedFromWaitList =
    currentStepName == "PersonalInfoFormStep" && waitlist?.contentId;

  useEffect(() => {
    if (error != null) {
      throw new Error(error);
    }
  }, [error]);

  useEffect(() => {
    (async function () {
      try {
        if (waitlist?.contentId) {
          setStepByName("PersonalInfoFormStep");
          setLoading(false);
        } else {
          const { treatments, services, centers, doctors } = await getRootData(
            bookingLabels
          );
          setRootData({
            treatments,
            services,
            centers,
            doctors,
          });

          if (
            ignorePersistedData ||
            isObjectEmpty(new StorageAccessor().getItem())
          ) {
            if (fixedService) {
              dispatch({
                type: FormReducer.update,
                field: FormFields.service,
                payload: fixedService,
              });

              dispatch({
                type: FormReducer.update,
                field: FormFields.treatment,
                payload: fixedService,
              });

              setStepByName("AppointmentStep");
            }

            let doctor: DoctorDto | undefined;
            if (doctorId) {
              const doctor = doctors.find((d) => d.id === doctorId);
              if (doctor) selectDoctorAndLocation(doctor, centers, dispatch);
            }

            let location: LocationDto | undefined;
            if (locationId) {
              const location = centers.find((c) => c.id === locationId);
              if (location) {
                dispatch({
                  type: FormReducer.update,
                  field: FormFields.location,
                  payload: location,
                });
              }
            }

            let service: ServiceContainerDto | undefined;

            if (containerId) {
              const container = findTreatmentById(treatments, containerId);
              selectContainer(
                container,
                treatments,
                dispatch,
                state,
                setStepByName,
                breadcrumbs
              );

              if (container) {
                if (isServiceContainer(container)) {
                  service = container;
                }
              }
            }
            GtmService.bookingOverlayLoaded(
              containerId,
              service?.bookingName,
              service?.id,
              location?.name,
              location?.id,
              doctor?.name,
              doctor?.id
            );
          }
        }
      } catch (err: any) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    window.history.pushState(
      null,
      `form-step-${currentStepRef.current}`,
      window.location.pathname + window.location.search
    );
    window.addEventListener("popstate", onBackButtonEvent);
    return () => {
      window.removeEventListener("popstate", onBackButtonEvent);
    };
  }, []);

  useEffect(() => {
    if (!waitlist) {
      dispatch({
        type: FormReducer.update,
        field: FormFields.currentStep,
        payload: currentStep,
      });
    }
  }, [currentStep]);

  if (!ignorePersistedData) {
    usePersistedSelection(dispatch, setStep, waitlist);
  }

  useEffect(() => {
    const element = titleRef;
    if (element && element.current && !modalOpenedFromWaitList) {
      element.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [currentStep]);

  function closeModalWithTracking() {
    if (currentStepName !== "BookingConfirmedStep") {
      GtmService.bookingDroppedOff();
    }
    closeModal();
  }

  function onBackButtonEvent(e: PopStateEvent) {
    e.preventDefault();
    if (autostartBooking || !showCloseButton) {
      window.history.back();
      return;
    }

    closeModalWithTracking();
  }

  const filteredTreatment = useFilteredTreatment(
    state.parentTreatment as TreatmentsContainerDto,
    state.location,
    state.doctor,
    !!newBookingFilterValues?.doctorId
  );

  const filteredTreatments = useFilteredTreatments(
    rootData.treatments,
    state.location,
    state.doctor,
    undefined,
    !!newBookingFilterValues?.doctorId
  );

  function goBackToParentOrInitial(
    filteredTreatment: ReturnType<typeof useFilteredTreatment>,
    rootData: RootDataType,
    dispatch: FormDispatch,
    setStepByName: (step: StepName) => void
  ): boolean {
    const singleItemTreatment = filteredTreatment.items.length === 1;
    const parentExistsInFilteredStructure = !!findTreatmentById(
      filteredTreatments,
      filteredTreatment.parentContainerId!
    );

    // Go to parent of parent if treatment is single item or parent does not exist in filtered structure
    if (singleItemTreatment || !parentExistsInFilteredStructure) {
      const parentTreatment =
        findClosestAvailableParentContainer(filteredTreatment);

      if (!!parentTreatment) {
        dispatch({
          type: FormReducer.update,
          field: FormFields.treatment,
          payload: parentTreatment,
        });

        const parentOfParentTreatment =
          findClosestAvailableParentContainer(parentTreatment);

        dispatch({
          type: FormReducer.update,
          field: FormFields.parentTreatment,
          payload: parentOfParentTreatment ?? parentTreatmentInitialState,
        });

        if (parentOfParentTreatment?.parentContainerId) {
          setStepByName("TreatmentStep");
        } else {
          setStepByName("InitialStep");
        }
      } else {
        setStepByName("InitialStep");
      }
      return true;
    }

    return false;
  }

  function findClosestAvailableParentContainer(
    container: TreatmentsContainerDto | ServiceContainerDto | null
  ): TreatmentsContainerDto | ServiceContainerDto | null {
    if (!container?.parentContainerId) {
      return null;
    }
    // Find parent container in the filtered structure
    let parentTreatment = findTreatmentById(
      filteredTreatments,
      container.parentContainerId
    );

    if (!parentTreatment) {
      // Find parent treatment in the full unfiltered structure
      parentTreatment = findTreatmentById(
        rootData.treatments,
        container.parentContainerId
      );

      if (parentTreatment == null) {
        return null;
      }

      return findClosestAvailableParentContainer(parentTreatment);
    }

    return parentTreatment;
  }

  const handleGoBackButtonClick = () => {
    dispatch({
      type: FormReducer.update,
      field: FormFields.cameFromBackNavigation,
      payload: true,
    });

    const goBackToParentTreatment = () => {
      GtmService.backButtonSelected(
        state.treatment.containerId,
        state.parentTreatment.containerId
      );
      if (state.parentTreatment.parentContainerId) {
        dispatch({
          type: FormReducer.update,
          field: FormFields.treatment,
          payload: state.parentTreatment,
        });
        setStepByName("TreatmentStep");
      } else {
        setStepByName("InitialStep");
      }
    };

    switch (currentStepName) {
      case "InitialStep":
        break;
      case "PersonalInfoFormStep":
      case "BookingConfirmedStep":
        return previousStep();
      case "AppointmentStep":
        dispatch({ type: FormReducer.clear, field: FormFields.service });
        dispatch({ type: FormReducer.clear, field: FormFields.appointment });
        dispatch({
          type: FormReducer.clear,
          field: FormFields.specializationName,
        });
        dispatch({ type: FormReducer.clear, field: FormFields.breadcrumbs });

        if (
          !goBackToParentOrInitial(
            filteredTreatment,
            rootData,
            dispatch,
            setStepByName
          )
        ) {
          goBackToParentTreatment();
        }
        break;
      case "TreatmentStep":
        if (
          !goBackToParentOrInitial(
            filteredTreatment,
            rootData,
            dispatch,
            setStepByName
          )
        ) {
          goBackToParentTreatment();
        }
        break;
      default:
        return assertNever(currentStepName);
    }
  };

  const setStepHeading = () => {
    if (
      currentStepName === "TreatmentStep" ||
      currentStepName === "AppointmentStep"
    ) {
      if (state.parentTreatment.parentContainerId) {
        return state.parentTreatment.bookingName;
      } else {
        return steps[0]?.heading;
      }
    }
    return steps[currentStep - 1]?.heading;
  };

  function determineOptionalText(
    stepName: StepName
  ): string | null | undefined {
    switch (stepName) {
      case "InitialStep":
        const labels = bookingLabels?.stepsBookingLabels.firstStepLabels;
        return newBookingFilterValues?.doctorId
          ? labels?.optionalDescriptionDoctors
          : labels?.optionalDescription;
      default:
        return null;
    }
  }

  const shouldDisplayGoBackButton =
    currentStepName !== "InitialStep" &&
    currentStepName !== "BookingConfirmedStep" &&
    !(currentStepName === "AppointmentStep" && fixedService);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    Modal.setAppElement(wrapperRef.current!);
  }, []);

  return (
    <div
      className={classNames("Booking", {
        "Booking--nimi": modalThemeColor == ModalThemeColor.Nimi,
      })}
    >
      <div className="Booking__navigation">
        {shouldDisplayGoBackButton && !loading && !waitlist && (
          <GoBackButton
            onClick={handleGoBackButtonClick}
            buttonLabel={setStepHeading()}
          />
        )}
        {showCloseButton && (
          <CloseButton
            onClick={closeModalWithTracking}
            className="Booking__closeButton"
          />
        )}
      </div>

      <div className="Booking__content">
        {loading && (
          <LoadingAnimation
            className="loading-animation-container text-center"
            text="Laster inn"
          />
        )}
        {!loading && (
          <ProgressBar
            currentStep={currentStep}
            currentStepName={currentStepName}
            totalSteps={steps.length}
            treatments={rootData.treatments}
            currentTreatment={state.treatment}
          />
        )}
        {!loading &&
          (() => {
            switch (currentStepName) {
              case "InitialStep":
                return (
                  <InitialStep
                    state={state}
                    dispatch={dispatch}
                    rootData={rootData}
                    quickAppointmentSetup={quickAppointmentSetup}
                    setStepByName={setStepByName}
                  />
                );
              case "TreatmentStep":
                return (
                  <TreatmentStep
                    state={state}
                    dispatch={dispatch}
                    nextStep={nextStep}
                    rootData={rootData}
                  />
                );
              case "AppointmentStep":
                return (
                  <AppointmentStep
                    state={state}
                    dispatch={dispatch}
                    nextStep={nextStep}
                    rootData={rootData}
                  />
                );
              case "PersonalInfoFormStep":
                return (
                  <PersonalInfoFormStep
                    state={state}
                    dispatch={dispatch}
                    nextStep={nextStep}
                    rootData={rootData}
                  />
                );
              case "BookingConfirmedStep":
                return (
                  <BookingConfirmedStep state={state} rootData={rootData} />
                );
              default:
                return null;
            }
          })()}
        {!loading && (
          <RichTextArea
            className="Booking__optionalText"
            content={determineOptionalText(currentStepName)}
          />
        )}
      </div>
    </div>
  );
};
